/**
 * A forest summary as the `forest_summary` step wrote it (forestum `docker/forest_summary`, formerly growing_stock):
 * `growing_stock.json` with the figures of every unit, and the `units` layer of `units.gpkg`
 * with their polygons. Both are small, so they are fetched whole on every page load.
 */
import { useEffect, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import type { LinkSource } from '@/map/linkSource';
import { loadLinkedGpkgLayers } from '@/map/vectorSource';
import { LEVELS, METHODS } from './config';

export const GROWING_STOCK_FILE = 'growing_stock.json';
export const UNITS_FILE = 'units.gpkg';
export const UNITS_TABLE = 'units';
/** Where the mobile scan reached, beside the units. */
export const MLS_AREA_TABLE = 'mls_area';
/** The areas the field crew surveyed, beside the units. */
export const FIELD_AREA_TABLE = 'field_area';


/** A PSK's record in the forest plan. */
export interface StandPlan {
  standId: string | null;
  areaHa: number | null;
  forestType: string | null;
  age: number | null;
  stocking: number | null;
  composition: string | null;
  description: string | null;
}

/** A PSK an MLS plot overlaps, with the share of the PSK's area inside the plot. */
export interface OverlapStand {
  standId: string;
  name: string;
  share: number | null;
}

/** How much of a unit the tree crowns cover (zápoj), snags included, shrubs not. */
export interface Canopy {
  crownHa: number;
  /** crownHa over the unit's covered area, 0..1; null when nothing of it is covered. */
  cover: number | null;
  /** ha of crown by genus, adding up to crownHa. */
  genera: Record<string, number>;
}

/** The basal area at breast height (výčetní kruhová základna, G) of one method in a unit. */
export interface BasalArea {
  /** m² of G in the unit. */
  m2: number;
  /** m² of G per m² of the method's per-hectare base (perHaBase); null without that area. ×10 000 → m²/ha. */
  rel: number | null;
  /** m² by genus. */
  byGenus: Record<string, number>;
}

export interface Unit {
  id: string;
  level: string;
  name: string;
  parent: string | null;
  areaHa: number | null;
  coveredHa: number | null;
  orthoCoverage: number | null;
  mlsHa: number | null;
  /** The part of the unit the field crew surveyed; 0 or null without field annotations. */
  fieldHa: number | null;
  /** m³ by method, then by genus as the step keys it (`Picea`, `snag`, …). */
  volumes: Record<string, Record<string, number>>;
  totals: Record<string, number>;
  perHa: Record<string, number>;
  plan: StandPlan | null;
  /** An MLS plot's PSKs; empty for every other unit. */
  stands: OverlapStand[];
  /** null in a summary written before the step measured it. */
  canopy: Canopy | null;
  /**
   * G by method, for the methods the step measured it for; a method null where its input lacks it
   * (never 0). Empty in a summary written before the step measured it (version 1).
   */
  basalArea: Record<string, BasalArea | null>;
}

export interface PlanHeader {
  source: string | null;
  lhcCode: string | null;
  name: string | null;
  validFrom: number | null;
  validTo: number | null;
  author: string | null;
}

export interface GrowingStock {
  /** Method key → the step's own label, in the order the step lists them. */
  methods: Record<string, string>;
  /** Genus key → the step's Czech name. */
  genera: Record<string, string>;
  levels: Record<string, string>;
  /** G method key → the step's own label (`basal_area_methods`); empty before version 2. */
  basalMethods: Record<string, string>;
  plan: PlanHeader | null;
  notes: string[];
  units: Unit[];
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : typeof v === 'number' && Number.isFinite(v) ? String(v) : null);
const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

function numbers(v: unknown): Record<string, number> {
  if (!isObject(v)) return {};
  return Object.fromEntries(Object.entries(v).flatMap(([k, x]) => (num(x) === null ? [] : [[k, num(x)!]])));
}

function labels(v: unknown, pick: (x: unknown) => unknown = (x) => x): Record<string, string> {
  if (!isObject(v)) return {};
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, str(pick(x)) ?? k]));
}

function overlaps(v: unknown): OverlapStand[] {
  return (Array.isArray(v) ? v : []).flatMap((s) => {
    if (!isObject(s) || str(s.stand_id) === null) return [];
    return [{ standId: str(s.stand_id)!, name: str(s.name) ?? str(s.stand_id)!, share: num(s.share) }];
  });
}

function standPlan(v: unknown): StandPlan | null {
  if (!isObject(v)) return null;
  // An MLS plot's plan names only the stands it overlaps; that is no stand record.
  if (['stand_id', 'forest_type', 'age', 'stocking', 'composition', 'description'].every((k) => v[k] === undefined || v[k] === null)) return null;
  return {
    standId: str(v.stand_id),
    areaHa: num(v.area_ha),
    forestType: str(v.forest_type),
    age: num(v.age),
    stocking: num(v.stocking),
    composition: str(v.composition),
    description: str(v.description),
  };
}

function canopy(v: unknown): Canopy | null {
  if (!isObject(v) || num(v.crown_ha) === null) return null;
  return { crownHa: num(v.crown_ha)!, cover: num(v.cover), genera: numbers(v.genera) };
}

function basalAreas(v: unknown): Record<string, BasalArea | null> {
  if (!isObject(v)) return {};
  return Object.fromEntries(
    Object.entries(v).map(([m, b]) => [m, isObject(b) && num(b.basal_area_m2) !== null ? { m2: num(b.basal_area_m2)!, rel: num(b.basal_area_rel), byGenus: numbers(b.by_genus) } : null]),
  );
}

function unit(v: unknown): Unit | null {
  if (!isObject(v) || str(v.id) === null) return null;
  const volumes = isObject(v.volumes) ? Object.fromEntries(Object.entries(v.volumes).map(([m, g]) => [m, numbers(g)])) : {};
  return {
    id: str(v.id)!,
    level: str(v.level) ?? '',
    name: str(v.name) ?? str(v.id)!,
    parent: str(v.parent),
    areaHa: num(v.area_ha),
    coveredHa: num(v.covered_ha),
    orthoCoverage: num(v.ortho_coverage),
    mlsHa: num(v.mls_ha),
    fieldHa: num(v.field_ha),
    volumes,
    totals: numbers(v.totals),
    perHa: numbers(v.per_ha),
    plan: standPlan(v.plan),
    // The step puts them beside the plan's record; on the unit itself is read too.
    stands: overlaps(isObject(v.plan) && Array.isArray(v.plan.stands) ? v.plan.stands : v.stands),
    canopy: canopy(v.canopy),
    basalArea: basalAreas(v.basal_area),
  };
}

/** Read leniently; null when there is no unit to show. */
export function growingStock(raw: unknown): GrowingStock | null {
  const r = isObject(raw) ? raw : {};
  const units = (Array.isArray(r.units) ? r.units : []).map(unit).filter((u): u is Unit => u !== null);
  if (!units.length) return null;
  const p = isObject(r.plan) ? r.plan : null;
  return {
    methods: labels(r.methods),
    genera: labels(r.genera, (x) => (isObject(x) ? x.label : x)),
    levels: labels(r.levels),
    basalMethods: labels(r.basal_area_methods),
    plan: p && {
      source: str(p.source),
      lhcCode: str(p.lhc_code),
      name: str(p.name),
      validFrom: num(p.valid_from),
      validTo: num(p.valid_to),
      author: str(p.author),
    },
    notes: (Array.isArray(r.notes) ? r.notes : []).filter((n): n is string => typeof n === 'string' && n !== ''),
    units,
  };
}

const levelRank = (level: string) => {
  const i = (LEVELS as readonly string[]).indexOf(level);
  return i < 0 ? LEVELS.length : i;
};
const byName = (a: Unit, b: Unit) => levelRank(a.level) - levelRank(b.level) || a.name.localeCompare(b.name, 'cs', { numeric: true });

export interface Hierarchy {
  byId: Map<string, Unit>;
  /** Units without a parent here: the whole area first, then each LHC. */
  roots: Unit[];
  children: (id: string) => Unit[];
  /** From the top down to the unit, the unit included. */
  path: (id: string) => Unit[];
}

/**
 * The units as a tree. The area and the LHCs both come without a parent; the area spans
 * them, so they are offered as its children and the breadcrumb of an LHC starts at the area.
 */
export function hierarchy(units: Unit[]): Hierarchy {
  const byId = new Map(units.map((u) => [u.id, u]));
  const kids = new Map<string | null, Unit[]>();
  for (const u of units) {
    const parent = u.parent !== null && byId.has(u.parent) ? u.parent : null;
    kids.set(parent, [...(kids.get(parent) ?? []), u]);
  }
  for (const list of kids.values()) list.sort(byName);
  const roots = kids.get(null) ?? [];
  const area = roots.find((u) => u.level === 'area');
  const children = (id: string) => (area && id === area.id ? roots.filter((u) => u !== area) : (kids.get(id) ?? []));
  const path = (id: string) => {
    const out: Unit[] = [];
    for (let u = byId.get(id); u && !out.includes(u); u = u.parent === null ? undefined : byId.get(u.parent)) out.unshift(u);
    if (area && out.length && out[0] !== area) out.unshift(area);
    return out;
  };
  return { byId, roots, children, path };
}

/** The methods to show, in the page's order: those chosen that the result holds, then any it holds the page does not know. */
export function shownMethods(gs: GrowingStock, chosen: Record<string, boolean>): string[] {
  const held = Object.keys(gs.methods);
  const known = (METHODS as readonly string[]).filter((m) => chosen[m] && held.includes(m));
  return [...known, ...held.filter((m) => !(METHODS as readonly string[]).includes(m))];
}

/** A genus as the colour palette and the species names key it: `snag` → `Snag`. */
export const genusKey = (genus: string) => genus.charAt(0).toUpperCase() + genus.slice(1);

/** The methods counted over the mobile scan's area rather than the orthophoto's. */
export const MLS_METHODS: readonly string[] = ['mls', 'mls_ortho'];
/** The methods counted over the field crew's surveyed area. */
export const FIELD_METHODS: readonly string[] = ['field'];

/** The area a method's stock is spread over for per-hectare figures: the step's own bases. */
export function perHaBase(u: Unit, method: string): number | null {
  if (MLS_METHODS.includes(method)) return u.mlsHa;
  if (FIELD_METHODS.includes(method)) return u.fieldHa;
  return u.coveredHa;
}

/** m³ → m³/ha for one method of a unit; null when it has no area to spread over. */
export function perHaFactor(u: Unit, method: string): number | null {
  const base = perHaBase(u, method);
  return base ? 1 / base : null;
}

const sumOf = (values: (number | null)[]) => (values.some((v) => v !== null) ? values.reduce<number>((s, v) => s + (v ?? 0), 0) : null);

/**
 * The units' G per method as one: summed (by genus too) and over the sum's own base, as the step
 * sums a parent. A method all of them lack stays null; one some lack counts the others'.
 */
function sumBasal(units: Unit[], sum: Unit): Record<string, BasalArea | null> {
  const out: Record<string, BasalArea | null> = {};
  for (const m of new Set(units.flatMap((u) => Object.keys(u.basalArea)))) {
    const held = units.flatMap((u) => (u.basalArea[m] ? [u.basalArea[m]!] : []));
    if (!held.length) {
      out[m] = null;
      continue;
    }
    const byGenus: Record<string, number> = {};
    for (const b of held) for (const [g, v] of Object.entries(b.byGenus)) byGenus[g] = (byGenus[g] ?? 0) + v;
    const m2 = held.reduce((s, b) => s + b.m2, 0);
    const base = perHaBase(sum, m);
    out[m] = { m2, rel: base ? m2 / (base * 1e4) : null, byGenus };
  }
  return out;
}

/** The units' canopy as one, over their summed covered area; null unless every one of them has it. */
function sumCanopy(units: Unit[], coveredHa: number | null): Canopy | null {
  if (!units.length || units.some((u) => u.canopy === null)) return null;
  const genera: Record<string, number> = {};
  for (const u of units) for (const [g, v] of Object.entries(u.canopy!.genera)) genera[g] = (genera[g] ?? 0) + v;
  const crownHa = units.reduce((s, u) => s + u.canopy!.crownHa, 0);
  return { crownHa, cover: coveredHa ? crownHa / coveredHa : null, genera };
}

/**
 * Each genus's share of a unit's covered area; together they make the cover. The genera in
 * `order` come first and in it, so several bars keep one place per genus; the rest largest first.
 */
export function canopyShares(c: Canopy, order: string[] = []): StackSegment[] {
  if (c.cover === null || c.crownHa <= 0) return [];
  const rank = (g: string) => (order.includes(g) ? order.indexOf(g) : order.length);
  return Object.entries(c.genera)
    .filter(([, v]) => v > 0)
    .map(([genus, v]) => ({ genus, value: (c.cover! * v) / c.crownHa }))
    .sort((a, b) => rank(a.genus) - rank(b.genus) || b.value - a.value || a.genus.localeCompare(b.genus));
}

/**
 * Several units as one, the way the step sums a parent from its children: the stock per method
 * and genus, the areas, and the per-hectare bases over all of them — a method one of them
 * lacks still spreads over the others' area too.
 */
export function sumUnits(units: Unit[], id: string, name: string): Unit {
  const volumes: Record<string, Record<string, number>> = {};
  for (const u of units) {
    for (const [m, genera] of Object.entries(u.volumes)) {
      const into = (volumes[m] ??= {});
      for (const [g, v] of Object.entries(genera)) into[g] = (into[g] ?? 0) + v;
    }
  }
  const totals = Object.fromEntries(Object.entries(volumes).map(([m, g]) => [m, Object.values(g).reduce((s, v) => s + v, 0)]));
  const coveredHa = sumOf(units.map((u) => u.coveredHa));
  // ortho_coverage is the covered share of each unit's polygon, so the polygons are recovered from it.
  const polygons = units.map((u) => (u.coveredHa !== null && u.orthoCoverage ? u.coveredHa / u.orthoCoverage : null));
  const polygonHa = polygons.every((v) => v !== null) ? sumOf(polygons) : null;
  const sum: Unit = {
    id,
    level: units.every((u) => u.level === units[0]?.level) ? (units[0]?.level ?? '') : '',
    name,
    parent: null,
    areaHa: sumOf(units.map((u) => u.areaHa)),
    coveredHa,
    orthoCoverage: coveredHa !== null && polygonHa ? Math.min(coveredHa / polygonHa, 1) : null,
    mlsHa: sumOf(units.map((u) => u.mlsHa)),
    fieldHa: sumOf(units.map((u) => u.fieldHa)),
    volumes,
    totals,
    perHa: {},
    plan: null,
    stands: [],
    canopy: sumCanopy(units, coveredHa),
    basalArea: {},
  };
  sum.basalArea = sumBasal(units, sum);
  sum.perHa = Object.fromEntries(Object.entries(totals).flatMap(([m, v]) => (perHaFactor(sum, m) === null ? [] : [[m, v * perHaFactor(sum, m)!]])));
  return sum;
}

/** The box around the polygons of the given units, as [west, south, east, north]; null when none is there. */
export function unitsBounds(fc: FeatureCollection, ids: Set<string>, idOf: (p: Record<string, unknown>) => string): [number, number, number, number] | null {
  let box: [number, number, number, number] | null = null;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      const [x, y] = c as [number, number];
      box = box ? [Math.min(box[0], x), Math.min(box[1], y), Math.max(box[2], x), Math.max(box[3], y)] : [x, y, x, y];
      return;
    }
    for (const inner of c) visit(inner);
  };
  for (const f of fc.features) {
    if (!ids.has(idOf((f.properties ?? {}) as Record<string, unknown>))) continue;
    if (f.geometry && 'coordinates' in f.geometry) visit(f.geometry.coordinates);
  }
  return box;
}

export interface StackSegment {
  genus: string;
  value: number;
}
export interface StackBar {
  method: string;
  segments: StackSegment[];
  total: number;
}

/**
 * One bar per method the unit has a figure for, its genera in one order across every bar:
 * the largest stock first, so the same genus sits in the same place on each.
 */
export function stackBars(u: Unit, methods: string[], perHa: boolean): StackBar[] {
  const present = methods.filter((m) => u.volumes[m] && Object.keys(u.volumes[m]).length);
  const weight = new Map<string, number>();
  for (const m of present) for (const [g, v] of Object.entries(u.volumes[m]!)) weight.set(g, (weight.get(g) ?? 0) + v);
  const order = [...weight.keys()].sort((a, b) => weight.get(b)! - weight.get(a)! || a.localeCompare(b));
  return present.flatMap((m) => {
    const scale = perHa ? perHaFactor(u, m) : 1;
    if (scale === null) return [];
    const vols = u.volumes[m]!;
    const segments = order.filter((g) => (vols[g] ?? 0) > 0).map((g) => ({ genus: g, value: vols[g]! * scale }));
    return [{ method: m, segments, total: segments.reduce((s, x) => s + x.value, 0) }];
  });
}

/** A unit's total for one method, absolute or per hectare; null when it has none. */
export function unitTotal(u: Unit, method: string, perHa: boolean): number | null {
  const total = u.totals[method];
  if (total === undefined) return null;
  if (!perHa) return total;
  const scale = perHaFactor(u, method);
  return scale === null ? null : total * scale;
}

/** The volume methods whose G is measured over the photo crowns: `ortho_dbh` stands beside them. */
const ORTHO_METHODS: readonly string[] = ['ortho_visible', 'ortho_under', 'ortho_all'];
/** G of the crowns' own DBH: no volume of its own, shown with the photo methods. */
export const ORTHO_DBH = 'ortho_dbh';

/**
 * The G methods to show beside each volume method shown: a method's own G after it, `ortho_dbh`
 * after the last photo method — only those the result holds (`basal_area_methods`, else what the
 * units carry). Empty for a summary without G (version 1).
 */
export function basalColumns(gs: GrowingStock, methods: string[]): Record<string, string[]> {
  const held = new Set([...Object.keys(gs.basalMethods), ...gs.units.flatMap((u) => Object.keys(u.basalArea))]);
  const lastOrtho = [...methods].reverse().find((m) => ORTHO_METHODS.includes(m));
  return Object.fromEntries(methods.map((m) => [m, [...(held.has(m) ? [m] : []), ...(m === lastOrtho && held.has(ORTHO_DBH) ? [ORTHO_DBH] : [])]]));
}

/** The G methods to show, in order (see basalColumns). */
export const shownBasalMethods = (gs: GrowingStock, methods: string[]): string[] => Object.values(basalColumns(gs, methods)).flat();

/** A unit's G for one method in m²/ha, or with `perHa` false in m²; null when it has none (or no area to spread it over). */
export function unitBasal(u: Unit, method: string, perHa = true): number | null {
  const b = u.basalArea[method];
  if (!b) return null;
  if (!perHa) return b.m2;
  return b.rel === null ? null : b.rel * 1e4;
}

/** One bar of G per method the unit has it for, by genus, in m²/ha (`perHa`) or m²; the genera in one order, largest first. */
export function basalBars(u: Unit, methods: string[], perHa: boolean): StackBar[] {
  const present = methods.filter((m) => u.basalArea[m] && Object.keys(u.basalArea[m]!.byGenus).length);
  const weight = new Map<string, number>();
  for (const m of present) for (const [g, v] of Object.entries(u.basalArea[m]!.byGenus)) weight.set(g, (weight.get(g) ?? 0) + v);
  const order = [...weight.keys()].sort((a, b) => weight.get(b)! - weight.get(a)! || a.localeCompare(b));
  return present.flatMap((m) => {
    const b = u.basalArea[m]!;
    const scale = perHa ? (b.rel === null || b.m2 <= 0 ? null : (b.rel * 1e4) / b.m2) : 1;
    if (scale === null) return [];
    const segments = order.filter((g) => (b.byGenus[g] ?? 0) > 0).map((g) => ({ genus: g, value: b.byGenus[g]! * scale }));
    return [{ method: m, segments, total: segments.reduce((s, x) => s + x.value, 0) }];
  });
}

export interface GrowingStockData {
  data: GrowingStock | null;
  units: FeatureCollection | null;
  /** The scan's outline; null when the step wrote none (no MLS input). */
  mlsArea: FeatureCollection | null;
  /** The surveyed areas' outlines; null when the step wrote none (no field annotations). */
  fieldArea: FeatureCollection | null;
  loading: boolean;
  /** growing_stock.json could not be read: there is nothing to show. */
  failed: boolean;
  /** The polygons could not be read: only the map is left out. */
  unitsFailed: boolean;
}

/** growing_stock.json per object, read once a session: the page asks again whenever its inputs settle. */
const figuresMemory = new Map<string, Promise<GrowingStock | null>>();

function readFigures(object: string, links: LinkSource): Promise<GrowingStock | null> {
  const held = figuresMemory.get(object);
  if (held) return held;
  const loading = links
    .url(object, GROWING_STOCK_FILE)
    .then((url) => fetch(url))
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`bucket ${r.status}`))))
    .then(growingStock);
  figuresMemory.set(object, loading);
  // A failed read is tried again next time.
  loading.catch(() => figuresMemory.delete(object));
  return loading;
}

/** Forget what this session read; for tests. */
export function forgetFigures(): void {
  figuresMemory.clear();
}

/** The figures and, when wanted, the units' polygons; `digest` keys the browser's cache of `units.gpkg`. */
export function useGrowingStockData(object: string | undefined, links: LinkSource, withUnits: boolean, digest?: string): GrowingStockData {
  const [figures, setFigures] = useState<{ object: string; data: GrowingStock | null } | null>(null);
  const [polygons, setPolygons] = useState<{
    key: string;
    units: FeatureCollection | null;
    mlsArea: FeatureCollection | null;
    fieldArea: FeatureCollection | null;
  } | null>(null);
  useEffect(() => {
    if (!object) return;
    let alive = true;
    readFigures(object, links).then(
      (data) => alive && setFigures({ object, data }),
      () => alive && setFigures({ object, data: null }),
    );
    return () => {
      alive = false;
    };
  }, [object, links]);
  // Read under the digest the grant gives, and not before it is known: the file is fetched once.
  const unitsKey = object && withUnits && digest !== undefined ? `${object}\u0000${digest}` : '';
  useEffect(() => {
    if (!unitsKey) return;
    const [o, d] = unitsKey.split('\u0000') as [string, string];
    let alive = true;
    loadLinkedGpkgLayers({ object: o, path: UNITS_FILE, digest: d }, links, [UNITS_TABLE, MLS_AREA_TABLE, FIELD_AREA_TABLE]).then(
      (tables) =>
        alive &&
        setPolygons({
          key: unitsKey,
          units: tables[UNITS_TABLE] ?? null,
          mlsArea: tables[MLS_AREA_TABLE] ?? null,
          fieldArea: tables[FIELD_AREA_TABLE] ?? null,
        }),
      () => alive && setPolygons({ key: unitsKey, units: null, mlsArea: null, fieldArea: null }),
    );
    return () => {
      alive = false;
    };
  }, [unitsKey, links]);
  const loaded = figures && figures.object === object ? figures : null;
  const shapes = polygons && polygons.key === unitsKey ? polygons : null;
  return {
    data: loaded?.data ?? null,
    units: shapes?.units ?? null,
    mlsArea: shapes?.mlsArea ?? null,
    fieldArea: shapes?.fieldArea ?? null,
    loading: !loaded,
    failed: !!loaded && loaded.data === null,
    unitsFailed: !!shapes && !shapes.units,
  };
}
